import React, { useEffect, useState } from "react";
import { Badge, IconButton, Popover, Tooltip, useTheme } from "@mui/material";
import NotificationsNoneRoundedIcon from "@mui/icons-material/NotificationsNoneRounded";
import NotificationsActiveRoundedIcon from "@mui/icons-material/NotificationsActiveRounded";
import { observer } from "mobx-react-lite";
import { alertsStore } from "../../../stores/alerts/AlertsStore";
import AlertsPanel from "./AlertsPanel";
import { getAlertColors } from "./alertUi";
import { trackAlertsAction } from "../../../../../analytics/alertsTracking";

interface AlertsBellProps {
  iconColor: string;
  iconHoverColor: string;
  iconHoverBg: string;
}

const AlertsBell: React.FC<AlertsBellProps> = ({
  iconColor,
  iconHoverColor,
  iconHoverBg,
}) => {
  const theme = useTheme();
  const colors = getAlertColors(theme);
  const c = theme.palette.customColors;
  const [anchorEl, setAnchorEl] = useState<HTMLElement | null>(null);

  useEffect(() => {
    alertsStore.startPolling();
    return () => alertsStore.stopPolling();
  }, []);

  const unread = alertsStore.totalUnread;
  const hasHigh = alertsStore.totalHighUnread > 0;
  const open = Boolean(anchorEl);
  const label =
    unread > 0
      ? `Alerts: ${unread} unread${hasHigh ? `, ${alertsStore.totalHighUnread} high priority` : ""}`
      : "Alerts";

  return (
    <>
      <Tooltip title={open ? "" : label}>
        <IconButton
          size="medium"
          aria-label={label}
          aria-haspopup="dialog"
          onClick={(event) => {
            setAnchorEl(event.currentTarget);
            trackAlertsAction("bell_open", "bell", {
              unreadCount: unread,
              highUnreadCount: alertsStore.totalHighUnread,
            });
          }}
          sx={{
            color: hasHigh ? c.errorRed : open ? iconHoverColor : iconColor,
            bgcolor: open ? iconHoverBg : "transparent",
            borderRadius: "8px",
            "&:hover": {
              bgcolor: iconHoverBg,
              color: hasHigh ? c.errorRed : iconHoverColor,
            },
          }}
        >
          <Badge
            badgeContent={unread}
            max={99}
            overlap="circular"
            sx={{
              "& .MuiBadge-badge": {
                minWidth: 17,
                height: 17,
                px: 0.5,
                fontSize: "0.62rem",
                fontWeight: 700,
                color: c.white,
                bgcolor: hasHigh ? c.errorRed : colors.accent,
                border: `2px solid ${colors.surface}`,
                top: 3,
                right: 3,
              },
            }}
          >
            {hasHigh ? (
              <NotificationsActiveRoundedIcon fontSize="small" />
            ) : (
              <NotificationsNoneRoundedIcon fontSize="small" />
            )}
          </Badge>
        </IconButton>
      </Tooltip>

      <Popover
        open={open}
        anchorEl={anchorEl}
        onClose={() => setAnchorEl(null)}
        anchorOrigin={{ vertical: "bottom", horizontal: "right" }}
        transformOrigin={{ vertical: "top", horizontal: "right" }}
        slotProps={{
          paper: {
            elevation: 0,
            sx: {
              mt: 1,
              borderRadius: "14px",
              overflow: "hidden",
              border: `1px solid ${colors.border}`,
              boxShadow: colors.shadow,
            },
          },
        }}
      >
        {open && <AlertsPanel onClose={() => setAnchorEl(null)} />}
      </Popover>
    </>
  );
};

export default observer(AlertsBell);
